import { useIntl } from 'react-intl';
import { CountryCodeType } from 'models/countries';
import { useMemo } from 'react';
import { useAppStore } from 'hooks/state/useAppStore';
import { useTransitiveCacheStore } from 'hooks/state/useTransitiveCacheStore';
import { TreeItemWithIcon } from 'components/treeselect/types';
import { TreeSelect } from 'components/treeselect/TreeSelect';
import { cn } from 'utils/classNamesTailwind';
import { Flag } from 'components/Flag';
import { TbIcon } from 'components/Figma';

interface CountrySelectProps {
  selectedCountries?: CountryCodeType[];
  setSelectedCountries?: (value: CountryCodeType[]) => void;
  className?: string;
  showConfirmButton?: boolean;
  disabled?: boolean;
}

export function CountrySelect({
  selectedCountries = undefined,
  setSelectedCountries = undefined,
  className = undefined,
  showConfirmButton = false,
  disabled = false,
}: CountrySelectProps) {
  const intl = useIntl();
  const { disabledFilters } = useTransitiveCacheStore();
  const isGlobalRegion = (countryCode: string) => countryCode.toLowerCase() === 'zz'; // global channel country code

  const { allCountries } = useTransitiveCacheStore();
  const { countries, setCountries } = useAppStore();

  const { countryTree, countryCodesById, expandedItems } = useMemo(() => {
    const regionRecord = new Map<string, TreeItemWithIcon>();
    const countryCodesById = new Map<string, CountryCodeType>();

    allCountries.forEach(country => {
      // A country may appear in multiple regions, but each tree row needs its own identity.
      // Repeated rows within one region must not create duplicate options either.
      const id = `country:${JSON.stringify([country.region, country.countryCode])}`;
      if (countryCodesById.has(id)) return;
      countryCodesById.set(id, country.countryCode);
      const label = isGlobalRegion(country.countryCode)
        ? intl.formatMessage({ id: 'filters.global', defaultMessage: 'Global' })
        : (intl.formatDisplayName(country.countryCode.toUpperCase() ?? 'DE', {
            type: 'region',
          }) ??
          country.countryCode.toUpperCase() ??
          'DE');
      const countryTreeItem = {
        id,
        label: label,
        icon: isGlobalRegion(country.countryCode) ? (
          <TbIcon name="Globe" />
        ) : (
          <Flag countryCode={country.countryCode} title={label} />
        ),
      };

      const region = regionRecord.get(country.region);
      if (region) {
        region.children!.push(countryTreeItem);
      } else {
        regionRecord.set(country.region, {
          id: `_${country.region}`,
          label: intl.formatMessage({
            id: `attribute.chn-region.${country.region}`,
            defaultMessage: country.region,
          }),
          children: [countryTreeItem],
        });
      }
    });

    const countryTree = [...regionRecord.values()].map(region =>
      region.children?.length === 1 ? region.children[0] : region
    );
    return {
      countryTree,
      countryCodesById,
      expandedItems: countryTree.filter(item => item.children?.length).map(item => item.id),
    };
  }, [allCountries, intl]);

  const selectedCountryAndRegions = useMemo(() => {
    const selected = new Set(selectedCountries ?? countries ?? []);
    const selection = [...countryCodesById].filter(([, code]) => selected.has(code)).map(([id]) => id);
    const selectedIds = new Set(selection);
    countryTree.forEach(region => {
      if (region.children?.length && region.children.every(child => selectedIds.has(child.id)))
        selection.push(region.id);
    });
    return selection;
  }, [countries, selectedCountries, countryTree, countryCodesById]);

  const regionAwareSetItems = (itemIds: string[]) => {
    const nextIds = new Set(itemIds);
    const removedCodes = new Set(
      selectedCountryAndRegions.filter(id => !nextIds.has(id)).map(id => countryCodesById.get(id))
    );
    const codes = itemIds
      .map(id => countryCodesById.get(id))
      .filter((code): code is CountryCodeType => code !== undefined && !removedCodes.has(code));
    (setSelectedCountries ?? setCountries)([...new Set(codes)]);
  };

  const isDisabled = disabled || disabledFilters.includes('chnCountry');

  return (
    <TreeSelect
      disabled={isDisabled}
      defaultExpandedItems={expandedItems}
      id="country-filter"
      className={cn(isDisabled ? 'disabled' : '', className)}
      label={intl.formatMessage({
        id: 'filter.key.selectedCountries',
      })}
      items={countryTree}
      selectedItems={selectedCountryAndRegions}
      setSelectedItems={regionAwareSetItems}
      showConfirmButton={showConfirmButton}
    />
  );
}
